import React, { memo, useEffect, useRef } from 'react';
import { AppNode, NodeType } from '@/types';
import { useDetailPageState } from '../../hooks/useDetailPageState';
import { useDetailPageActions, cleanupSkillResultNodes } from '../../hooks/useDetailPageActions';
import { askActionRegistry, DetailPageAskSnapshot } from './DetailPageAskPopover';
import { DetailPagePipelineView } from './DetailPagePipelineView';

interface DetailPageContentProps {
  data: AppNode;
  selected: boolean;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onAddNode?: (type: NodeType, position?: { x: number; y: number }, initialData?: Record<string, unknown>) => string | undefined;
  /** 统一生成编排入口下行：分块出图创建节点后自动触发生图 */
  onNodeAction?: (id: string, prompt?: string) => void;
  /** 询问快照上行（仅在 pendingAsk 引用变化时触发），驱动外壳弹窗与 zIndex 抬升 */
  onPendingAskChange?: (snapshot: DetailPageAskSnapshot | null) => void;
}

const DetailPageContentInner: React.FC<DetailPageContentProps> = ({
  data,
  onNodeUpdate,
  onAddNode,
  onNodeAction,
  onPendingAskChange,
}) => {
  const {
    stage,
    planner,
    designer,
    generatedBlocks,
    updatePlanner,
  } = useDetailPageState(data, onNodeUpdate);

  const {
    plannerSession,
    designerSession,
    syncProductImageNodes,
    syncCompetitorImageNodes,
    skipCompetitorAnalysis,
    syncSellingPointNode,
    runPlannerGeneration,
    runDesignerGeneration,
    generateBlockImage,
    confirmGenerateSplitPromptNodes,
    regenerateDesignerPrompts,
    reparseDesignerPrompts,
  } = useDetailPageActions({ data, onNodeUpdate, onAddNode, onNodeAction });

  // 存量 skill-result 结果节点一次性清理（§9 迁移）：ref 守卫防 StrictMode 双跑与重渲染重复执行
  const cleanupRef = useRef(false);
  useEffect(() => {
    if (cleanupRef.current) return;
    cleanupRef.current = true;
    cleanupSkillResultNodes(data.id);
  }, [data.id]);

  // 存量分屏提示词回填（解析契约修复）：promptResult 已有全文但 splitPrompts 为空时补写（函数内含幂等与运行态守卫）
  const reparseRef = useRef(false);
  useEffect(() => {
    if (reparseRef.current) return;
    reparseRef.current = true;
    reparseDesignerPrompts();
  }, [reparseDesignerPrompts]);

  // 询问快照源（§6.6）：会话未挂载/已复位时回退持久化 runState（同 PipelineView effectiveRunState 逻辑）；
  // designer 优先（E 防御：链式串行下两者理论不同时挂起，同时存在时较新者为准）
  const effectivePlannerRunState = plannerSession.runState.status === 'idle' && planner?.runState
    ? planner.runState
    : plannerSession.runState;
  const effectiveDesignerRunState = designerSession.runState.status === 'idle' && designer?.runState
    ? designer.runState
    : designerSession.runState;
  const designerAsk = effectiveDesignerRunState.pendingAsk;
  const plannerAsk = effectivePlannerRunState.pendingAsk;
  const activeAsk = designerAsk ?? plannerAsk;

  // 快照上行：effect 仅在 pendingAsk 引用变化时触发，避免流式渲染高频 setState 死循环
  const onPendingAskChangeRef = useRef(onPendingAskChange);
  onPendingAskChangeRef.current = onPendingAskChange;
  useEffect(() => {
    onPendingAskChangeRef.current?.(activeAsk ? { kind: designerAsk ? 'designer' : 'planner', ask: activeAsk } : null);
  }, [activeAsk, designerAsk]);

  // 动作桥下行：渲染期按节点 id 注册最新闭包（挂起会话的 reply/cancel），
  // 弹窗回调按 id 路由到所属节点的会话，多节点并存时互不串扰；无挂起时注销防陈旧闭包残留
  const activeSession = designerAsk ? designerSession : plannerSession;
  if (activeAsk) {
    askActionRegistry.set(data.id, { reply: activeSession.reply, cancel: activeSession.cancel });
  } else {
    askActionRegistry.delete(data.id);
  }

  return (
    <DetailPagePipelineView
      stage={stage}
      planner={planner}
      designer={designer}
      generatedBlocks={generatedBlocks}
      plannerSession={plannerSession}
      designerSession={designerSession}
      onUpdatePlanner={updatePlanner}
      onSyncProductImageNodes={syncProductImageNodes}
      onSyncCompetitorImageNodes={syncCompetitorImageNodes}
      onSkipCompetitor={skipCompetitorAnalysis}
      onSyncSellingPointNode={syncSellingPointNode}
      onRunPlanner={runPlannerGeneration}
      onRunDesigner={runDesignerGeneration}
      onGenerateBlock={generateBlockImage}
      onGenerateAll={confirmGenerateSplitPromptNodes}
      onRegeneratePrompts={regenerateDesignerPrompts}
      nodeTitle={data.title}
    />
  );
};

export const DetailPageContent = memo(DetailPageContentInner, (prev, next) => {
  return (
    prev.data.id === next.data.id &&
    prev.data.data === next.data.data &&
    prev.data.title === next.data.title &&
    prev.data.inputs?.length === next.data.inputs?.length &&
    prev.data.status === next.data.status &&
    prev.selected === next.selected
  );
});
